import Alert from "@mui/material/Alert";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { PageLayout } from "@/shared/components";
import { EventRecord } from "@/shared/domain/events/EventRecord";
import { eventQueryKeys } from "@/shared/domain/events/hooks";
import { QueryContentMode, QueryTarget } from "@/shared/domain/query";
import {
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { useEventTablePreferences } from "@/shared/domain/users/hooks";
import {
  DataTable,
  createColumnHelper,
  useDataTableState,
  type DataTableLoadingState,
} from "@/shared/foundation/primitives/Table";
import { formatNanoTimestamp } from "@/shared/time";
import type { SearchQueryBody, SortDirection } from "@/types";

import styles from "./EventsList.module.css";

const columnHelper = createColumnHelper<EventRecord>();

const columns = [
  columnHelper.accessor("event_id", {
    headerName: "ID",
    width: 180,
    cellClass: styles.monoCell,
  }),
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 1,
    sortable: true,
  }),
  columnHelper.accessor("description", {
    headerName: "Description",
    flex: 3,
  }),
  columnHelper.accessor("start_time", {
    headerName: "Start",
    width: 200,
    sortable: true,
    cellClass: styles.monoCell,
    valueFormatter: (p) => formatNanoTimestamp(p.value),
  }),
  columnHelper.accessor("end_time", {
    headerName: "End",
    width: 200,
    sortable: true,
    cellClass: styles.monoCell,
    valueFormatter: (p) => formatNanoTimestamp(p.value),
  }),
];

export function EventsList() {
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const { preferences, savePreferences } = useEventTablePreferences();

  const tableState = useDataTableState({
    initialSort: { columnId: "start_time", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
  });

  const query: SearchQueryBody = tableState.sort
    ? {
        sort_by: tableState.sort.columnId,
        sort_direction:
          tableState.sort.direction.toUpperCase() as SortDirection,
      }
    : {};

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Events,
    contentMode: QueryContentMode.RecordOnly,
    queryKey: eventQueryKeys.list(resolvedOrgId, query),
  });
  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<EventRecord>(
    queryId,
    tableState.currentPage,
    tableState.pageSize,
  );
  const countQuery = useQueryCount(queryId);

  const rows = pageQuery.data ?? [];

  const renderExpandedRow = React.useCallback(
    (row: EventRecord) => <EventDetailFields event={row} />,
    [],
  );

  let loading: DataTableLoadingState = "idle";
  if (submitQuery.isLoading || pageQuery.isLoading) {
    loading = "foreground";
  } else if (submitQuery.isFetching || pageQuery.isFetching) {
    loading = "background";
  }
  const fetchError = submitQuery.error ?? pageQuery.error;

  return (
    <PageLayout title="Roboto - Events" headerContentLeft="Events">
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {fetchError.message}
        </Alert>
      )}
      <DataTable.Root>
        <DataTable.Table<EventRecord>
          {...tableState.table}
          hasNextPage={pageQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          getRowId={(row) => row.event_id}
          noRowsContent="No events"
          totalRowCount={countQuery.data}
          selectionEnabled
          expansion={{ renderRow: renderExpandedRow }}
        />
      </DataTable.Root>
    </PageLayout>
  );
}

function formatDetailValue(key: string, value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className={styles.detailEmpty}>—</span>;
  }
  if (key === "start_time" || key === "end_time") {
    return formatNanoTimestamp(value as bigint);
  }
  if (typeof value === "bigint") {
    return value.toString();
  }
  if (typeof value === "string") {
    if (value === "") {
      return <span className={styles.detailEmpty}>—</span>;
    }
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value) && value.length === 0) {
    return <span className={styles.detailEmpty}>—</span>;
  }
  if (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  ) {
    return <span className={styles.detailEmpty}>—</span>;
  }
  return (
    <pre className={styles.detailJson}>
      {JSON.stringify(
        value,
        (_, v: unknown) => (typeof v === "bigint" ? v.toString() : v),
        2,
      )}
    </pre>
  );
}

function EventDetailFields({ event }: { event: EventRecord }) {
  const entries = Object.entries(event);
  return (
    <div
      className={styles.detailFields}
      role="region"
      aria-label={`Details for event ${event.event_id}`}
    >
      {entries.map(([key, value]) => (
        <div key={key} className={styles.detailField}>
          <div className={styles.detailKey}>{key}</div>
          <div className={styles.detailValue}>
            {formatDetailValue(key, value)}
          </div>
        </div>
      ))}
    </div>
  );
}
